import { useEffect, useRef, useState } from "react";
import { FieldExtensionComponentProps, makeFieldSchema, useTemplateSecrets } from "@backstage/plugin-scaffolder-react";
import { z } from 'zod';
import { ErrorPanel, Progress } from '@backstage/core-components';
import { useAsync } from 'react-use';
import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import { substituteJmesPathValueTemplate, substituteUrlTemplate } from "../helpers";
import { search as jmespathSearch } from '@jmespath-community/jmespath';

const uiOptionsSchema = z.object({
    tokenSecret: z.string().describe('The name of the secret that contains the token to use for the API data fetcher').optional(),
    authMethod: z.enum(['bearer']).describe('The method to use for authentication. Will only be used if a token is provided.').default('bearer'),
    url: z.string().describe('The URL to fetch the data from.'),
    jmesPath: z.string().describe('JMESPath to process the data.').optional(),
    substituteJmesPathTemplate: z.boolean().describe("Whether to treat jmesPath as a template and substitute form field values into it before evaluating it. Placeholders use {{fieldName}} (double curly braces — distinct from JMESPath's own single-brace {key: value} multi-select-hash syntax, so real JMESPath is never mistaken for a placeholder). Substituted values are escaped for safe use inside a JMESPath string literal, but you must add your own quotes for string comparisons (e.g. [?name=='{{fullName}}']) and omit them for numeric/boolean comparisons (e.g. [?age>{{minAge}}]). This has no effect on url, which is always substituted using {fieldName} placeholders regardless.").optional(),
});

export const ApiDataLoaderFieldSchema = makeFieldSchema({
	output: zImpl => zImpl.object({}),
	uiOptions: zImpl => zImpl.object({
    tokenSecret: zImpl.string().describe('The name of the secret that contains the token to use for the API data fetcher').optional(),
    authMethod: zImpl.enum(['bearer']).describe('The method to use for authentication. Will only be used if a token is provided.').default('bearer'),
    url: zImpl.string().describe('The URL to fetch the data from.'),
    jmesPath: zImpl.string().describe('JMESPath to process the data.').optional(),
    substituteJmesPathTemplate: zImpl.boolean().describe("Whether to treat jmesPath as a template and substitute form field values into it before evaluating it. Placeholders use {{fieldName}} (double curly braces — distinct from JMESPath's own single-brace {key: value} multi-select-hash syntax, so real JMESPath is never mistaken for a placeholder). Substituted values are escaped for safe use inside a JMESPath string literal, but you must add your own quotes for string comparisons (e.g. [?name=='{{fullName}}']) and omit them for numeric/boolean comparisons (e.g. [?age>{{minAge}}]). This has no effect on url, which is always substituted using {fieldName} placeholders regardless.").optional(),
  }),
});



export const asyncTimeout = (ms: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
};

const throwError = (message: string) => {
    throw new Error(message);
}


export const ApiDataLoaderField = (props: FieldExtensionComponentProps<object, z.infer<typeof uiOptionsSchema>>) => {
    const { onChange } = props;
    const { formData } = props.formContext;
    const { secrets } = useTemplateSecrets();
    const fetchApi = useApi(fetchApiRef);
    const discoveryApi = useApi(discoveryApiRef);
    const uiOptions = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);

    // Provide fallback values so hooks are always called unconditionally.
    // The real values are only used when uiOptions.success is true (guarded below).
    const { tokenSecret, authMethod, url, jmesPath, substituteJmesPathTemplate } = uiOptions.success
        ? uiOptions.data
        : { tokenSecret: undefined, authMethod: 'bearer' as const, url: '', jmesPath: undefined, substituteJmesPathTemplate: false };

    const [ debouncedFormData, setDebouncedFormData ] = useState(formData);
    const finalUrlRef = useRef('');
    const tokenRef = useRef('');

    useEffect(() => {
        if (tokenSecret) {
            const token = secrets[tokenSecret];
            tokenRef.current = token;
            return;
        }
        tokenRef.current = '';
    });

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedFormData(formData);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [formData]);

    const { error, loading } = useAsync(async () => {
        // Do nothing until the UI options are valid.
        if (!uiOptions.success) return;

        const { finalUrl, isValid, missingProperties } = substituteUrlTemplate(url, debouncedFormData);

        if (!isValid) {
            throwError(`Missing required properties: ${missingProperties.join(', ')}`);
        }

        if (tokenSecret && !tokenRef.current) {
            throwError(`Token secret ${tokenSecret} not found`);
        }

        // Resolve relative URLs against the Backstage backend base URL so that
        // proxy paths (e.g. /api/proxy/…) work both locally (port 7007) and in
        // production where frontend and backend share the same port.
        let resolvedUrl = finalUrl;
        if (finalUrl.startsWith('/')) {
            // Extract only the origin (scheme + host + port) so that the path
            // in finalUrl (e.g. /api/proxy/…) is not doubled up.
            const { origin } = new URL(await discoveryApi.getBaseUrl('proxy'));
            resolvedUrl = `${origin}${finalUrl}`;
        }

        // Skip if the URL hasn't changed
        if (resolvedUrl !== finalUrlRef.current) {
            finalUrlRef.current = resolvedUrl;
        } else {
            return;
        }

        let headers = {};
        switch (authMethod) {
            case 'bearer':
                headers = {
                    Authorization: `Bearer ${tokenRef.current}`
                }
                break;
            default:
                throwError(`Unsupported auth method: ${authMethod}`);
        }

        try {
            const response = await fetchApi.fetch(resolvedUrl, {
                method: 'GET',
                    headers
                });

            if (!response.ok) {
                onChange(undefined);
                throwError(`Failed to fetch data: ${response.status} ${response.statusText}`);
            }

            let data = await response.json();
            if (jmesPath) {
                if (substituteJmesPathTemplate) {
                    const { result: finalJmesPath, isValid, missingProperties } = substituteJmesPathValueTemplate(jmesPath, debouncedFormData);
                    if (!isValid) {
                        throwError(`Missing required properties: ${missingProperties.join(', ')}`);
                    }
                    data = jmespathSearch(data, finalJmesPath);
                } else {
                    data = jmespathSearch(data, jmesPath);
                }
            }
            onChange(data);
        } catch (fetchError) {
            onChange(undefined);
            throw fetchError;
        }
    }, [url, debouncedFormData, tokenRef]);

    // Early returns come after all hooks.
    if (!uiOptions.success) {
        return <ErrorPanel error={uiOptions.error} />;
    }

    if (error) {
        return <ErrorPanel error={error} />;
    }

    if (loading) {
        return <Progress />;
    }

    return <></>
}
